import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import type { Session, User as SupabaseUser } from '@supabase/supabase-js'
import type { Json } from '../types/supabase'
import { getDeviceId, getDeviceType, getDeviceInfo, type DeviceType } from '../utils/deviceFingerprint'
import { useToast } from './ToastContext'
import { useNavigationState } from '../hooks/useNavigationState'
import * as Sentry from '@sentry/react'

interface User {
    id: string
    name: string
    email: string
    avatar: string
    status: 'online' | 'busy' | 'offline'
}

interface SessionConflict {
    deviceType: DeviceType
    existingDeviceId: string
    lastActive: string
}

interface UserContextType {
    user: User | null
    session: Session | null
    isLoading: boolean
    updateUser: (user: Partial<User>) => void
    logout: () => Promise<void>
    loginWithGoogle: () => Promise<void>
    signIn: (email: string, password: string) => Promise<void>
    signUp: (email: string, password: string, name?: string) => Promise<void>
    theme: 'light' | 'dark'
    toggleTheme: () => void
    isSettingsOpen: boolean
    openSettings: () => void
    closeSettings: () => void
    activeSettingsTab: 'general' | 'llm' | 'voice' | 'devices'
    setActiveSettingsTab: (tab: 'general' | 'llm' | 'voice' | 'devices') => void
    sessionConflict: SessionConflict | null
    resolveSessionConflict: (action: 'replace' | 'cancel') => Promise<void>
}

// eslint-disable-next-line react-refresh/only-export-components
export const UserContext = createContext<UserContextType | undefined>(undefined)

// Cookie utilities for theme persistence
function setCookie(name: string, value: string, days: number = 365) {
    const expires = new Date()
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`
}

function getCookie(name: string): string | null {
    const nameEQ = name + "="
    const ca = document.cookie.split(';')
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i]
        while (c.charAt(0) === ' ') c = c.substring(1, c.length)
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
    }
    return null
}

export function UserProvider({ children }: { children: ReactNode }) {
    const { showToast } = useToast()

    // Load theme from cookie first (synchronous), then from Supabase
    // Cookies are read before page render, preventing FOUC completely
    const getInitialTheme = (): 'light' | 'dark' => {
        try {
            const cached = getCookie('theme')
            if (cached === 'light' || cached === 'dark') {
                return cached
            }
        } catch (e) {
            console.error('[Theme] Error reading from cookie:', e)
        }
        return 'dark' // Default fallback
    }

    const [theme, setTheme] = useState<'light' | 'dark'>(getInitialTheme())
    const [isThemeLoaded, setIsThemeLoaded] = useState(false)
    const [isThemeUserModified, setIsThemeUserModified] = useState(false)

    const [user, setUser] = useState<User | null>(null)
    const [session, setSession] = useState<Session | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [sessionConflict, setSessionConflict] = useState<SessionConflict | null>(null)

    // Navigation persistence (passed user to avoid circular dependency)
    const { getPageState, setPageState } = useNavigationState(user)

    // Settings modal state - using LOCAL state (not persisted)
    const [isSettingsOpen, setIsSettingsOpen] = useState(false)
    const settingsState = getPageState('/settings')
    const [activeSettingsTab, setActiveSettingsTabLocal] = useState<'general' | 'llm' | 'voice' | 'devices'>('general')

    // Sync activeSettingsTab when settingsState loads from database
    useEffect(() => {
        if (settingsState?.activeTab) {
            // eslint-disable-next-line
            setActiveSettingsTabLocal(settingsState.activeTab as 'general' | 'llm' | 'voice' | 'devices')
        }
    }, [settingsState])

    const mapSupabaseUser = (supabaseUser: SupabaseUser) => {
        setUser({
            id: supabaseUser.id,
            name: supabaseUser.user_metadata.full_name || supabaseUser.email?.split('@')[0] || 'User',
            email: supabaseUser.email || '',
            avatar: supabaseUser.user_metadata.avatar_url || 'https://github.com/shadcn.png',
            status: 'online'
        })
    }

    // Load theme from Supabase on mount
    useEffect(() => {
        async function loadTheme() {
            console.log('[Theme] loadTheme called, user:', user?.id, 'isThemeLoaded:', isThemeLoaded)

            if (!user) {
                // Wait for user to be available before loading theme
                console.log('[Theme] No user yet, waiting...')
                return
            }

            console.log('[Theme] Fetching theme from Supabase for user:', user.id)
            const { data, error } = await supabase
                .from('user_preferences')
                .select('theme')
                .eq('user_id', user.id)
                .single()

            if (error) {
                console.log('[Theme] Error loading theme:', error)
            }

            if (data?.theme) {
                console.log('[Theme] Loaded theme from DB:', data.theme)
                const dbTheme = data.theme as 'light' | 'dark'
                setTheme(dbTheme)
                // Update cookie cache
                setCookie('theme', dbTheme)
            } else {
                console.log('[Theme] No theme preference found, defaulting to dark')
                setTheme('dark')
                setCookie('theme', 'dark')
            }
            setIsThemeLoaded(true)
        }

        if (!isThemeLoaded) {
            loadTheme()
        }
    }, [user, isThemeLoaded])

    // Apply theme to DOM and save to Supabase when it changes
    useEffect(() => {
        console.log('[Theme] Apply effect triggered, theme:', theme, 'isThemeLoaded:', isThemeLoaded)

        const root = document.documentElement
        if (theme === 'dark') {
            root.classList.add('dark')
        } else {
            root.classList.remove('dark')
        }

        // Save to Supabase if user is logged in and theme is loaded AND user modified it
        async function saveTheme() {
            if (!user || !isThemeLoaded || !isThemeUserModified) {
                console.log('[Theme] Not saving - user:', user?.id, 'isThemeLoaded:', isThemeLoaded, 'isThemeUserModified:', isThemeUserModified)
                return
            }

            console.log('[Theme] Saving theme to Supabase:', theme, 'for user:', user.id)
            const { error } = await supabase
                .from('user_preferences')
                .upsert({
                    user_id: user.id,
                    theme
                }, { onConflict: 'user_id' })

            if (error) {
                console.error('[Theme] Error saving theme:', error)
            } else {
                console.log('[Theme] Theme saved successfully')
                // Also update cookie cache
                setCookie('theme', theme)
            }
        }

        saveTheme()
    }, [theme, user, isThemeLoaded, isThemeUserModified])

    useEffect(() => {
        // Get initial session
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session)
            if (session?.user) {
                mapSupabaseUser(session.user)
            } else {
                setUser(null)
            }
            setIsLoading(false)
        }).catch((error) => {
            console.error('[Auth] Error getting session:', error)
            setIsLoading(false)
        })

        // Listen for auth changes
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session)
            if (session?.user) {
                mapSupabaseUser(session.user)
            } else {
                setUser(null)
            }
            setIsLoading(false)
        })

        return () => subscription.unsubscribe()
    }, [])

    // Set Sentry user context when user changes
    useEffect(() => {
        if (user) {
            Sentry.setUser({
                id: user.id,
                email: user.email,
                username: user.name,
            })
        } else {
            Sentry.setUser(null)
        }
    }, [user])

    const updateUser = (updates: Partial<User>) => {
        if (user) {
            setUser({ ...user, ...updates })
            // Here you would typically update the user in Supabase database as well
        }
    }

    const signIn = async (email: string, password: string) => {
        const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
        })
        if (error) throw error
    }

    const signUp = async (email: string, password: string, name?: string) => {
        const { error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    full_name: name,
                }
            }
        })
        if (error) throw error
    }

    const loginWithGoogle = async () => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
        })
        if (error) throw error
    }

    const logout = async () => {
        const { error } = await supabase.auth.signOut()
        if (error) throw error
        setUser(null)
        setSession(null)
    }

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light')
        setIsThemeUserModified(true)
    }

    // Settings modal functions - using local state (not persisted)
    const handleOpenSettings = () => {
        setIsSettingsOpen(true)
    }

    const handleCloseSettings = () => {
        setIsSettingsOpen(false)
    }

    const handleSetActiveSettingsTab = (tab: 'general' | 'llm' | 'voice' | 'devices') => {
        setActiveSettingsTabLocal(tab)
        setPageState('/settings', { activeTab: tab })
    }

    const resolveSessionConflict = async (action: 'replace' | 'cancel') => {
        if (!user || !sessionConflict) return

        if (action === 'cancel') {
            await logout()
            setSessionConflict(null)
        } else {
            // Replace
            const deviceType = getDeviceType()
            const deviceId = getDeviceId()
            const deviceInfo = getDeviceInfo()

            // Delete existing session for this type
            await supabase
                .from('active_sessions')
                .delete()
                .eq('user_id', user.id)
                .eq('device_type', deviceType)

            // Insert new session
            await supabase
                .from('active_sessions')
                .insert({
                    user_id: user.id,
                    device_type: deviceType,
                    device_id: deviceId,
                    device_info: deviceInfo as unknown as Json
                })

            setSessionConflict(null)
        }
    }

    // Register session on login
    useEffect(() => {
        async function registerSession() {
            if (!user) return

            const deviceType = getDeviceType()
            const deviceId = getDeviceId()
            const deviceInfo = getDeviceInfo()

            // Check existing session
            const { data: existingSession } = await supabase
                .from('active_sessions')
                .select('*')
                .eq('user_id', user.id)
                .eq('device_type', deviceType)
                .maybeSingle()

            if (existingSession) {
                if (existingSession.device_id === deviceId) {
                    // It's me! Update last_active
                    await supabase
                        .from('active_sessions')
                        .update({
                            last_active: new Date().toISOString(),
                            device_info: deviceInfo as unknown as Json
                        })
                        .eq('id', existingSession.id)
                } else {
                    // It's someone else! Conflict!
                    setSessionConflict({
                        deviceType,
                        existingDeviceId: existingSession.device_id,
                        lastActive: existingSession.last_active
                    })
                }
            } else {
                // No session exists, create one
                await supabase
                    .from('active_sessions')
                    .insert({
                        user_id: user.id,
                        device_type: deviceType,
                        device_id: deviceId,
                        device_info: deviceInfo as unknown as Json
                    })
            }
        }

        if (user && !isLoading) {
            registerSession()
        }
    }, [user, isLoading])

    // Listen for session changes (remote logout)
    useEffect(() => {
        if (!user) return

        const deviceId = getDeviceId()
        const channel = supabase
            .channel(`session_monitor_${user.id}`)
            .on('postgres_changes', {
                event: 'DELETE',
                schema: 'public',
                table: 'active_sessions',
                filter: `user_id=eq.${user.id}`
            }, (payload) => {
                // Check if MY session was deleted
                if (payload.old.device_id === deviceId) {
                    logout()
                    showToast('info', 'Você foi desconectado por outro dispositivo')
                }
            })
            .subscribe()

        return () => {
            supabase.removeChannel(channel)
        }
    }, [user, showToast])

    return (
        <UserContext.Provider value={{
            user,
            session,
            isLoading,
            updateUser,
            logout,
            loginWithGoogle,
            signIn,
            signUp,
            theme,
            toggleTheme,
            isSettingsOpen,
            openSettings: handleOpenSettings,
            closeSettings: handleCloseSettings,
            activeSettingsTab,
            setActiveSettingsTab: handleSetActiveSettingsTab,
            sessionConflict,
            resolveSessionConflict
        }}>
            {children}
        </UserContext.Provider>
    )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUser() {
    const context = useContext(UserContext)
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider')
    }
    return context
}
